import React, { useEffect, useState } from 'react';
import UserService from '../services/UserService';
import HelpButton from './HelpButton';
import chatbotService from '../services/ChatbotService';
import UserForm from './UserForm';

import {
    Box,
    Typography,
    TextField,
    Paper,
    Button,
    Avatar,
    Snackbar,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Switch,
    MenuItem,
    Select,
    FormControl,
    Tooltip,
    InputAdornment,
    Chip,
} from '@mui/material';
import IconButton from '@mui/material/IconButton';
import SearchIcon       from '@mui/icons-material/Search';
import EditIcon         from '@mui/icons-material/Edit';
import DeleteIcon       from '@mui/icons-material/Delete';
import LoginIcon        from '@mui/icons-material/Login';
import SchoolIcon       from '@mui/icons-material/School';
import TuneIcon         from '@mui/icons-material/Tune';

import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

// ─── palette (mirrors sidebar) ───────────────────────────────────────────────
const C = {
    crimson:      '#287bd6',
    crimsonLight: '#3f8fe0',
    blue:         '#1976d2',
    ink:          '#222222',
    muted:        '#6f6f6f',
    border:       '#e0e0e0',
    cardBg:       '#ffffff',
    pageBg:       '#ffffff',
    avatarBg:     '#bdbdbd',
    icon:         '#2b2b2b',
    iconMuted:    '#747474',
};

const formatDate = (value) => {
    if (!value) return '-';
    const raw = typeof value === 'number' ? value * 1000 : value;
    const date = new Date(raw);
    if (Number.isNaN(date.getTime())) return '-';
    return new Intl.DateTimeFormat('ja-JP', { year: 'numeric', month: '2-digit', day: '2-digit' }).format(date);
};

// ─── UserCard ────────────────────────────────────────────────────────────────
const UserCard = ({ user, profile, t, onEdit, onDelete, onToggle, onLoginAs, onNavigate }) => {
    const initial = user.name?.charAt(0).toUpperCase() || '?';

    return (
        <Paper
            elevation={0}
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 3,
                minHeight: 126,
                px: 2.4,
                py: 1.8,
                borderRadius: '3px',
                backgroundColor: C.cardBg,
                boxShadow: '0 3px 10px rgba(0,0,0,0.08)',
                transition: 'box-shadow 0.18s ease',
                '&:hover': {
                    boxShadow: '0 5px 14px rgba(0,0,0,0.11)',
                },
            }}
        >
            {/* Left: avatar + info */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0 }}>
                <Avatar
                    sx={{
                        bgcolor: C.avatarBg,
                        width: 50,
                        height: 50,
                        fontSize: '1.35rem',
                        fontWeight: 400,
                        flexShrink: 0,
                    }}
                >
                    {initial}
                </Avatar>
                <Box sx={{ minWidth: 0 }}>
                    <Typography
                        sx={{ fontSize: '1rem', fontWeight: 700, color: C.ink, lineHeight: 1.35, mb: 0.25 }}
                        noWrap
                    >
                        {user.name}
                    </Typography>
                    <Typography sx={{ fontSize: '0.92rem', color: C.muted, lineHeight: 1.35 }} noWrap>
                        {user.email}
                    </Typography>
                    <Typography sx={{ fontSize: '0.92rem', color: C.muted, lineHeight: 1.35 }} noWrap>
                        {t('users.phoneNumber', 'Phone Number')}{user.phoneNumber ? ` ${user.phoneNumber}` : ''}
                    </Typography>
                    <Typography sx={{ fontSize: '0.82rem', color: C.muted, lineHeight: 1.35 }} noWrap>
                        {t('users.createdDate', 'Created Date')}: {formatDate(user.createDate)}
                    </Typography>
                    {profile === 'member' && (
                        <Box sx={{ mt: 0.6, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {(() => {
                                const ids = Array.isArray(user.permissionGroupIds) ? user.permissionGroupIds : [];
                                const names = Array.isArray(user.permissionGroupNames) ? user.permissionGroupNames : [];
                                if (ids.length === 0 && names.length === 0) {
                                    return <Chip size="small" label={t('users.fullAccessDefault', 'Full access')} sx={{ height: 22, fontSize: '0.72rem', fontWeight: 600, color: C.muted, bgcolor: 'rgba(0,0,0,0.06)' }} />;
                                }
                                const source = ids.length ? ids : names;
                                return source.map((id, i) => {
                                    const fallback = ids.length ? (names[i] || id) : id;
                                    return <Chip key={id} size="small" label={t(`permissionGroups.groupNames.${id}`, fallback)} sx={{ height: 22, fontSize: '0.72rem', fontWeight: 600, color: '#1f6fca', bgcolor: 'rgba(40,123,214,0.12)' }} />;
                                });
                            })()}
                        </Box>
                    )}
                </Box>
            </Box>

            {/* Right: actions */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.05, flexShrink: 0 }}>
                {profile === 'customer' && (
                    <Tooltip title={t('users.bases', 'Bases')} placement="top" arrow>
                        <IconButton size="small" onClick={() => onNavigate(`/admin/clients/${encodeURIComponent(user.name)}/bases`)}
                            sx={{ color: C.crimson, '&:hover': { bgcolor: 'rgba(40,123,214,0.08)' } }}>
                            <SchoolIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                )}
                {profile === 'customer' && (
                    <Tooltip title={t('users.config', 'Config')} placement="top" arrow>
                        <IconButton size="small" onClick={() => onNavigate(`/admin/clients/${encodeURIComponent(user.name)}/config/chatbots`)}
                            sx={{ color: C.iconMuted, '&:hover': { bgcolor: 'rgba(0,0,0,0.05)' } }}>
                            <TuneIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                )}

                <Tooltip title={t('users.edit', 'Edit')} placement="top" arrow>
                    <IconButton size="small" onClick={() => onEdit(user)}
                        sx={{ color: C.icon, '&:hover': { bgcolor: 'rgba(0,0,0,0.05)' } }}>
                        <EditIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
                <Tooltip title={t('users.delete', 'Delete')} placement="top" arrow>
                    <IconButton size="small" onClick={() => onDelete(user)}
                        sx={{ color: C.icon, '&:hover': { bgcolor: 'rgba(0,0,0,0.05)' } }}>
                        <DeleteIcon fontSize="small" />
                    </IconButton>
                </Tooltip>

                <Tooltip title={user.enabled ? t('users.enabled', 'Enabled') : t('users.disabled', 'Disabled')} placement="top" arrow>
                    <Switch
                        size="small"
                        checked={!!user.enabled}
                        onChange={() => onToggle(user)}
                        sx={{
                            mx: -0.5,
                            '& .MuiSwitch-switchBase.Mui-checked': { color: '#ffffff' },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#8a8a8a', opacity: 1 },
                            '& .MuiSwitch-track': { bgcolor: '#9a9a9a', opacity: 1 },
                        }}
                    />
                </Tooltip>

                {profile === 'customer' && (
                    <Tooltip title={t('users.loginAs', 'Login As')} placement="top" arrow>
                        <IconButton size="small" onClick={() => onLoginAs(user.name)}
                            sx={{
                                color: C.iconMuted,
                                '&:hover': { bgcolor: 'rgba(0,0,0,0.05)' },
                            }}>
                            <LoginIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                )}
            </Box>
        </Paper>
    );
};

// ─── main component ───────────────────────────────────────────────────────────
function Users({ handleLoginAs, profile }) {
    const navigate = useNavigate();
    const [users,          setUsers]          = useState([]);
    const [searchQuery,    setSearchQuery]    = useState('');
    const [sortBy,         setSortBy]         = useState('name');
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [openSnackbar,   setOpenSnackbar]   = useState(false);
    const [snackSeverity,  setSnackSeverity]  = useState('success');
    const { t } = useTranslation();
    const [openFormDialog, setOpenFormDialog] = useState(false);
    const [dialogType,     setDialogType]     = useState('');
    const [selectedUser,   setSelectedUser]   = useState(null);
    const [formBusy,       setFormBusy]       = useState(false);

    const openAdd    = ()      => { setFormBusy(false); setDialogType('add');    setOpenFormDialog(true); };
    const openDelete = (user)  => { setFormBusy(false); setDialogType('delete'); setSelectedUser(user); setOpenFormDialog(true); };
    const openEdit   = (user)  => { setFormBusy(false); setDialogType('edit');   setSelectedUser(user); setOpenFormDialog(true); };

    const handleLoginAsWithNav = async (username) => {
        handleLoginAs(username);
        try {
            const list = await chatbotService.getEnabledFeatures(username);
            const enabled = new Set(Array.isArray(list) ? list : []);
            if (enabled.has('voiceflow.support')) {
                navigate('/data-management');
            } else if (enabled.has('nebot.support')) {
                navigate('/documents');
            } else {
                navigate('/customer-list');
            }
        } catch {
            navigate('/customer-list');
        }
    };
    const closeDialog = ()     => {
        if (formBusy) return;
        setOpenFormDialog(false);
        setDialogType('');
        setSelectedUser(null);
        setFormBusy(false);
    };

    const finishFormDialog = () => {
        setFormBusy(false);
        setOpenFormDialog(false);
        setDialogType('');
        setSelectedUser(null);
        fetchUsers();
    };

    const fetchUsers = async () => {
        try {
            setUsers(await UserService.getUsers(profile));
        } catch {
            toast('Failed to load users.', 'error');
        }
    };

    useEffect(() => { fetchUsers(); }, [profile]); // eslint-disable-line react-hooks/exhaustive-deps

    const toast = (msg, severity = 'success') => {
        setSnackbarMessage(msg);
        setSnackSeverity(severity);
        setOpenSnackbar(true);
    };

    const handleConfirmDelete = async () => {
        try {
            await UserService.deleteUser(selectedUser.name);
            setUsers((prev) => prev.filter((u) => u.name !== selectedUser.name));
            closeDialog();
            toast(`${selectedUser.name} deleted.`);
        } catch {
            toast('Failed to delete user.', 'error');
        }
    };

    const handleToggleEnabled = async (user) => {
        try {
            await UserService.enableUser(user.name, !user.enabled);
            setUsers((prev) => prev.map((u) => u.name === user.name ? { ...u, enabled: !u.enabled } : u));
            toast(`${user.name} ${!user.enabled ? 'enabled' : 'disabled'}.`);
        } catch {
            toast('Failed to update user.', 'error');
        }
    };

    const sortUsers = (list) => [...list].sort((a, b) =>
        sortBy === 'date'
            ? Number(b.createDate || 0) - Number(a.createDate || 0)
            : a.name.localeCompare(b.name)
    );

    const filteredUsers = sortUsers(
        users.filter((u) => {
            const q = searchQuery.toLowerCase();
            return [u.name, u.email, u.phoneNumber].some((value) => String(value || '').toLowerCase().includes(q));
        })
    );

    const pageTitle =
        profile === 'admin'    ? t('sideBar.adminSetting')        :
        profile === 'customer' ? t('sideBar.customerList')        :
                                 t('sideBar.memberList');

    const addLabel =
        profile === 'admin'    ? t('users.addSystemAdmin') :
        profile === 'customer' ? t('users.addClient')      :
                                 t('users.addMember');

    return (
        <Box
            sx={{
                mx: -3,
                mt: -3,
                mb: -3,
                width: 'calc(100% + 48px)',
                px: { xs: 2, md: 5 },
                pt: 4,
                pb: 3,
                backgroundColor: C.pageBg,
                minHeight: '100vh',
            }}
        >
            {/* ── Page header + toolbar in one row ── */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    mb: 3.2,
                    flexWrap: 'wrap',
                }}
            >
                {/* Title block */}
                <Box sx={{ minWidth: 220, mr: 'auto' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <Typography sx={{ fontSize: '1.35rem', fontWeight: 700, color: '#000000', lineHeight: 1.2 }}>
                            {pageTitle}
                        </Typography>
                        <HelpButton />
                    </Box>
                </Box>

                {/* Search */}
                <TextField
                    size="small"
                    placeholder={t('users.searchPlaceholder', 'search for customer...')}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon sx={{ fontSize: 22, color: '#7a7a7a' }} />
                            </InputAdornment>
                        ),
                    }}
                    sx={{
                        width: { xs: '100%', sm: 290 },
                        '& .MuiOutlinedInput-root': {
                            borderRadius: '4px',
                            backgroundColor: C.cardBg,
                            fontSize: '1rem',
                            '& fieldset': { borderColor: '#cfcfcf' },
                            '&:hover fieldset': { borderColor: '#bdbdbd' },
                            '&.Mui-focused fieldset': { borderColor: '#9e9e9e' },
                        },
                    }}
                />

                {/* Sort */}
                <FormControl size="small">
                    <Select
                        value={sortBy}
                        onChange={(e) => setSortBy(e.target.value)}
                        displayEmpty
                        sx={{
                            borderRadius: '4px',
                            backgroundColor: C.cardBg,
                            minWidth: 170,
                            color: C.ink,
                            '& fieldset': { borderColor: '#cfcfcf' },
                            '&:hover fieldset': { borderColor: '#bdbdbd' },
                            '&.Mui-focused fieldset': { borderColor: '#9e9e9e' },
                        }}
                    >
                        <MenuItem value="name">{t('users.sortByName', 'By Name')}</MenuItem>
                        <MenuItem value="date">{t('users.sortByDate', 'By Date')}</MenuItem>
                    </Select>
                </FormControl>

                {/* Add button */}
                <Button
                    variant="contained"
                    onClick={openAdd}
                    sx={{
                        borderRadius: '4px',
                        fontWeight: 500,
                        px: 2,
                        minHeight: 46,
                        whiteSpace: 'nowrap',
                        textTransform: 'uppercase',
                        background: C.blue,
                        boxShadow: '0 2px 5px rgba(0,0,0,0.25)',
                        '&:hover': {
                            background: '#1565c0',
                            boxShadow: '0 3px 7px rgba(0,0,0,0.28)',
                        },
                    }}
                >
                    {addLabel}
                </Button>
            </Box>

            {/* ── User list ── */}
            {filteredUsers.length === 0 ? (
                <Paper elevation={0} sx={{ textAlign: 'center', py: 8, borderRadius: '10px', border: `1px dashed ${C.border}` }}>
                    <Typography sx={{ color: C.muted, fontSize: '0.9rem' }}>
                        {searchQuery ? 'No matching records found.' : 'No users yet.'}
                    </Typography>
                </Paper>
            ) : (
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                    }}
                >
                    {filteredUsers.map((user) => (
                        <UserCard
                            key={user.name}
                            user={user}
                            profile={profile}
                            t={t}
                            onEdit={openEdit}
                            onDelete={openDelete}
                            onToggle={handleToggleEnabled}
                            onLoginAs={handleLoginAsWithNav}
                            onNavigate={navigate}
                        />
                    ))}
                </Box>
            )}

            {/* ── Dialog ── */}
            <Dialog
                open={openFormDialog}
                onClose={closeDialog}
                disableEscapeKeyDown={formBusy}
                maxWidth={dialogType === 'add' && profile === 'customer' ? 'lg' : 'sm'}
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: '12px',
                        border: `1px solid ${C.border}`,
                        boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
                    },
                }}
            >
                <DialogTitle
                    sx={{
                        fontWeight: 700,
                        fontSize: '1.05rem',
                        color: C.ink,
                        borderBottom: `1px solid ${C.border}`,
                        pb: 1.5,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                    }}
                >
                    <Box sx={{ width: 3, height: 18, borderRadius: 1, bgcolor: C.crimson, flexShrink: 0 }} />
                    {dialogType === 'delete' && `${t('users.deleteConfirmationTitle')}${selectedUser?.name}`}
                    {dialogType === 'edit'   && profile === 'customer' && `${t('users.editClientTitle')}${selectedUser?.name}`}
                    {dialogType === 'edit'   && profile === 'admin'    && `${t('users.editSystemAdminTitle')}${selectedUser?.name}`}
                    {dialogType === 'add'    && profile === 'customer' && t('users.addClientTitle')}
                    {dialogType === 'add'    && profile === 'admin'    && t('users.addSystemAdminTitle')}
                    {dialogType === 'add'    && profile === 'member'   && t('users.addMember')}
                </DialogTitle>

                {dialogType === 'delete' && (
                    <DialogActions sx={{ justifyContent: 'space-between', px: 3, py: 2, borderTop: `1px solid ${C.border}` }}>
                        <Button onClick={closeDialog} sx={{ color: C.muted, borderRadius: '8px', textTransform: 'none' }}>
                            {t('users.cancel')}
                        </Button>
                        <Button
                            onClick={handleConfirmDelete}
                            variant="contained"
                            sx={{
                                borderRadius: '8px',
                                textTransform: 'none',
                                background: `linear-gradient(135deg, ${C.crimson} 0%, #1f6fca 100%)`,
                                boxShadow: `0 2px 8px rgba(40,123,214,0.35)`,
                                '&:hover': { background: `linear-gradient(135deg, ${C.crimsonLight} 0%, ${C.crimson} 100%)` },
                            }}
                        >
                            {t('users.confirmDelete')}
                        </Button>
                    </DialogActions>
                )}

                {(dialogType === 'add' || dialogType === 'edit') && (
                    <DialogContent sx={{ p: 3 }}>
                        <UserForm
                            key={dialogType === 'edit' ? selectedUser?.name : profile}
                            operType={dialogType}
                            profile={profile}
                            user={dialogType === 'edit' ? selectedUser : undefined}
                            onCancel={closeDialog}
                            onBusyChange={setFormBusy}
                            onUserAddOrUpdate={finishFormDialog}
                        />
                    </DialogContent>
                )}
            </Dialog>

            {/* ── Snackbar ── */}
            <Snackbar open={openSnackbar} autoHideDuration={3000} onClose={() => setOpenSnackbar(false)}>
                <Alert severity={snackSeverity} onClose={() => setOpenSnackbar(false)} sx={{ width: '100%' }}>
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </Box>
    );
}

export default Users;
